import React, { useState, useEffect } from 'react';
import { bolusnotesApi } from '../services/api';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';

interface DexcomTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  dexcomUsername?: string;
  dexcomPassword?: string;
}

const DexcomTestModal: React.FC<DexcomTestModalProps> = ({ isOpen, onClose, dexcomUsername, dexcomPassword }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (isOpen && dexcomUsername && dexcomPassword) {
      setLoading(true);
      setErrorMsg('');
      
      bolusnotesApi.dexcom.test(dexcomUsername, dexcomPassword)
        .then(readings => {
          // Flatten latest 3h mapped data for recharts
          const mapped = readings.map((r: any) => ({
            time: new Date(parseInt(r.WT.match(/\d+/)[0])).toLocaleTimeString([], { hour: '2-digit', minute:'2-digit' }),
            value: r.Value,
            trend: r.Trend
          })).reverse(); // Oldest to newest
          setData(mapped);
          setLoading(false);
        })
        .catch(err => {
          setErrorMsg(err?.response?.data?.error || err.message || 'Unknown network error during test.');
          setLoading(false);
        });
    }
  }, [isOpen, dexcomUsername, dexcomPassword]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal-content profile-modal" style={{ maxWidth: '600px' }}>
         <div className="modal-header">
           <h2>Dexcom Connection Test</h2>
           <button className="btn-close" onClick={onClose}>×</button>
         </div>
         
         <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {loading && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                 <h3>Authenticating with Dexcom Share...</h3>
                 <p className="help-text">Please wait. Retrieving the last 3 hours of glucose data.</p>
              </div>
            )}

            {!loading && errorMsg && (
              <div style={{ backgroundColor: '#ffeee6', border: '1px solid #ff5c5c', padding: '20px', borderRadius: '8px', color: '#ff3b30' }}>
                 <h4 style={{ margin: '0 0 10px 0'}}>Connection Failed</h4>
                 <p>{errorMsg}</p>
                 <p style={{ marginTop: '10px', fontSize: '0.9em' }}>Please double-check your username and password.</p>
              </div>
            )}

            {!loading && !errorMsg && data.length > 0 && (
              <div style={{ width: '100%', height: '300px' }}>
                <h4 style={{ marginBottom: '15px', textAlign: 'center', color: 'var(--text-primary)' }}>Success! Latest Readings</h4>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} stroke="var(--border-medium)" />
                    <XAxis dataKey="time" tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
                    <YAxis domain={['auto', 'auto']} tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
                    <Tooltip 
                       contentStyle={{ backgroundColor: 'var(--surface-primary)', border: '1px solid var(--border-medium)' }}
                       labelStyle={{ color: 'var(--text-secondary)' }}
                       itemStyle={{ color: 'var(--brand-primary)', fontWeight: 'bold' }}
                    />
                    <Line type="monotone" dataKey="value" stroke="var(--brand-primary)" strokeWidth={3} dot={{ r: 2 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            
            {!loading && !errorMsg && data.length === 0 && (
              <p>Authenication succeeded, but no glucose data was found in the last 3 hours.</p>
            )}
         </div>

         <div className="modal-actions">
           <button type="button" className="btn-primary" onClick={onClose} style={{ marginLeft: 'auto' }}>
             Done
           </button>
         </div>
      </div>
    </div>
  );
};

export default DexcomTestModal;
